import { useState } from 'react'

import { nanoid } from 'nanoid'

import { ClientError, ClientFileError } from '@tribeplatform/gql-client/lib'
import {
  useCreateEmojis,
  useCreateImages,
} from '@tribeplatform/react-sdk/hooks'

import { mergeArrayByKey } from '../../common/utils/array.utils.js'
import { useUploadQueue } from '../../Providers/UploadQueueProvider.js'
import { BaseEmoji } from '../../types/index.js'
import { UploadableImage, UploadableMedia } from './types.js'

export type UseHandleUplodableMediaProps = {
  defaultValue: UploadableMedia[]
  multiple: boolean
  onChange: (images: UploadableMedia[]) => void
  onUploadStart?: (images: UploadableMedia[]) => void
  onError: (error: ClientFileError) => void
}

export const useHandleUploadableMedia = ({
  defaultValue,
  multiple,
  onChange,
  onUploadStart,
  onError,
}: UseHandleUplodableMediaProps) => {
  const [images, setImages] = useState<UploadableMedia[]>(defaultValue)

  const { addToUploadQueue, removeFromUploadQueue } = useUploadQueue()
  const { mutateAsync: createImages, isLoading: isImagesLoading } =
    useCreateImages({
      useMutationOptions: {
        onMutate: () => {
          onUploadStart?.(images)
        },
        onError,
      },
    })
  const { mutateAsync: createEmojis, isLoading: isEmojisLoading } =
    useCreateEmojis()

  const onImageUpload = async (files: File[]) => {
    if (!files?.length) {
      return null
    }
    if (!multiple) {
      files = files.slice(0, 1)
    }

    const tempImageData = await Promise.all(
      files.map(async file => {
        const fileSrc = URL.createObjectURL(file)

        return {
          id: nanoid(),
          name: file.name,
          file,
          isLoading: true,
          url: fileSrc,
        } as UploadableImage
      }),
    )

    setImages(images =>
      multiple ? mergeArrayByKey(images, tempImageData, 'name') : tempImageData,
    )

    /**
     * Since we are using a single mutation to upload multiple files
     * and the request for all files will fail or resolve together,
     * we will assign a signle upload queue item to all files.
     */
    const batchUploadQueueItem = {
      id: nanoid(),
    }
    addToUploadQueue(batchUploadQueueItem)

    try {
      const uploadedImages = await createImages(
        files.map(file => ({
          name: file.name,
          file,
        })),
      )

      const updatedImages = multiple
        ? mergeArrayByKey(images, uploadedImages, 'name')
        : uploadedImages
      setImages(updatedImages)
      onChange(updatedImages)
    } catch (error) {
      // Revert back
      const updatedImages = [...images.filter(a => !a.isLoading)]
      setImages(updatedImages)
      onChange(updatedImages)

      const message: string =
        (error as ClientError)?.response?.errors
          ?.map(e => e.message)
          ?.join('\n') || error.message

      onError({
        fileName: '',
        message,
      })
    } finally {
      removeFromUploadQueue(batchUploadQueueItem)
    }
  }

  const onEmojiChange = async (emoji: BaseEmoji) => {
    try {
      const uploadedEmoji = await createEmojis([{ text: emoji?.id }])
      setImages(uploadedEmoji)
      onChange(uploadedEmoji)
    } catch (error) {
      const message: string =
        (error as ClientError)?.response?.errors
          ?.map(e => e.message)
          ?.join('\n') || error.message

      onError({
        fileName: '',
        message,
      })
    }
  }

  const onRemove = (image: UploadableImage) => {
    const updatedImages = [...images.filter(it => it.id !== image.id)]
    setImages(updatedImages)
    onChange(updatedImages)
  }

  return {
    medias: images,
    isLoading: isImagesLoading || isEmojisLoading,
    onImageUpload,
    onEmojiChange,
    onRemove,
  }
}
